import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import * as numbers from "common/numbers";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import { DEFAULT_TARGET_LOSS_RATIO } from "components/lossRating/lossRatingConstants";
import { useEffect, useState } from "react";

const FONT_SIZE = "0.875rem";

const SectionHeader = ({ label, open, onToggle }) => (
  <TableRow
    sx={{ backgroundColor: "grey.100", cursor: "pointer" }}
    onClick={onToggle}
  >
    <TableCell colSpan={6}>
      <Typography
        component={"div"}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          fontSize={FONT_SIZE}
          fontWeight={700}
          color={"text.secondary"}
        >
          {label}
        </Typography>
        {open ? (
          <ExpandLessIcon sx={{ fontSize: 16, color: "text.secondary" }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 16, color: "text.secondary" }} />
        )}
      </Typography>
    </TableCell>
  </TableRow>
);

const CollapsibleSection = ({ open, children }) => (
  <TableRow sx={{ p: 0 }}>
    <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
      <Collapse in={open} timeout={"auto"} unmountOnExit>
        <Table size={"small"} sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableBody>{children}</TableBody>
        </Table>
      </Collapse>
    </TableCell>
  </TableRow>
);

const CascadeRow = ({ label, value, bold }) => (
  <TableRow>
    <TableCell
      colSpan={4}
      align={"right"}
      sx={{
        fontSize: FONT_SIZE,
        fontWeight: bold ? 700 : 400,
        color: bold ? "text.primary" : "text.secondary",
        border: 0,
        py: 0.5,
      }}
    >
      {label}
    </TableCell>
    <TableCell
      colSpan={2}
      align={"right"}
      sx={{
        fontSize: FONT_SIZE,
        fontWeight: bold ? 700 : 600,
        color: "text.primary",
        border: 0,
        py: 0.5,
      }}
    >
      {value}
    </TableCell>
  </TableRow>
);

const InputRow = ({ label, value, onChange, bold }) => (
  <TableRow>
    <TableCell
      colSpan={4}
      align={"right"}
      sx={{
        fontSize: FONT_SIZE,
        fontWeight: bold ? 700 : 400,
        color: bold ? "text.primary" : "text.secondary",
        border: 0,
        py: 0.25,
      }}
    >
      {label}
    </TableCell>
    <TableCell colSpan={2} align={"right"} sx={{ border: 0, py: 0.25 }}>
      <PrettyNumberTextField
        hiddenLabel
        fullWidth
        size={"small"}
        variant={"filled"}
        value={value ?? null}
        onChangeNumber={onChange}
        sx={{
          "& .MuiInputBase-input": {
            fontSize: FONT_SIZE,
            textAlign: "right",
            py: "4px",
            px: 1,
            ...(bold ? { fontWeight: 700 } : {}),
          },
        }}
      />
    </TableCell>
  </TableRow>
);

const LossRatingResults = ({ values, data, onUpdate }) => {
  const [open, setOpen] = useState(true);
  const [premiumOverride, setPremiumOverride] = useState(null);

  useEffect(() => setPremiumOverride(null), [values]);

  if (!values) return null;

  const { records = [], summary = {} } = values;
  const avgUl = summary.avg_ultimate_loss ?? 0;
  const engineTlr = summary.target_loss_ratio ?? DEFAULT_TARGET_LOSS_RATIO;
  const enginePremium = summary.needed_premium_1st_dollar ?? 0;
  const storedTlr = data?.targetLossRatio ?? engineTlr;

  const displayTlr =
    premiumOverride != null && avgUl > 0 ? avgUl / premiumOverride : storedTlr;
  const displayPremium =
    premiumOverride ?? (storedTlr > 0 ? avgUl / storedTlr : enginePremium);

  const handleTlrChange = (v) => {
    onUpdate("targetLossRatio", v == null ? null : v / 100);
    setPremiumOverride(null);
  };

  const includedRecords = records.filter((r) => r.include);
  const avgExpFactor =
    includedRecords.length > 0
      ? includedRecords.reduce((s, r) => s + (r.exposure_factor ?? 1), 0) /
        includedRecords.length
      : null;

  return (
    <TableContainer>
      <Table size={"small"} sx={{ tableLayout: "fixed", width: "100%" }}>
        <TableBody>
          <SectionHeader
            label={"Loss Rating Summary"}
            open={open}
            onToggle={() => setOpen((v) => !v)}
          />
          <CollapsibleSection open={open}>
            <CascadeRow
              label={"Avg. Claims / Year (incl.)"}
              value={numbers.dpString(1)(summary.avg_claims_per_year)}
            />
            {avgExpFactor != null && (
              <CascadeRow
                label={"Avg. Exposure Factor (incl.)"}
                value={numbers.dpString(2)(avgExpFactor)}
              />
            )}
            <CascadeRow
              label={"Avg. Ultimate Loss (incl.)"}
              value={numbers.currencyFormat(avgUl, 0)}
            />
            <InputRow
              label={"Target Loss Ratio (%)"}
              value={
                displayTlr != null ? Math.round(displayTlr * 10000) / 100 : null
              }
              onChange={handleTlrChange}
            />
            <InputRow
              label={"Needed Premium (1st $)"}
              value={Math.round(displayPremium) || null}
              onChange={(v) => setPremiumOverride(v)}
              bold
            />
          </CollapsibleSection>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default LossRatingResults;
